import React, { useEffect } from "react";
import {
    Alert,
    Paper,
    Skeleton,
    Typography,
    Box,
    Divider,
    Button,
    Grid,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GavelIcon from '@mui/icons-material/Gavel';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ListAltIcon from '@mui/icons-material/ListAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import { usePncpBiddingDetailController } from "./context/pncp-bidding-detail-controller";
import { usePncpBiddingDetailState } from "./context/pncp-bidding-detail-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import dayjs from "dayjs";

const formatDate = (value?: string, format = 'DD/MM/YYYY') => (value ? dayjs(value).format(format) : '-');

const formatCurrency = (value?: number) =>
    value == null ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const openExternal = (url?: string) => {
    if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};

function InfoCard({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {icon}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {label}
                        </Typography>
                        {children}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export function PncpBiddingDetailComponent() {
    const { loadBiddingDetail, loadItems } = usePncpBiddingDetailController();
    const { biddingDetail, loading, error, items, itemsPage, hasMoreItems, loadingItems, itemsError } = usePncpBiddingDetailState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            loadBiddingDetail(Number(id));
            loadItems(Number(id), 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const purchase = biddingDetail?.purchase;
    const number = purchase
        ? `${purchase.numberPurchase || purchase.sequence}/${purchase.yearPurchase}`
        : (loading ? 'Carregando...' : 'Licitação');
    const documents = biddingDetail?.documents ?? [];

    const backButton = (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/licitacoes')}
                size="large"
            >
                Voltar para licitações
            </Button>
        </Box>
    );

    return (
        <Box>
            <PageHeader
                title={number}
                subtitle="Detalhes da Licitação"
                breadcrumbs={[
                    { label: 'Licitações', href: '/licitacoes' },
                    { label: number }
                ]}
            />

            {loading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={200} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : error || !purchase ? (
                <React.Fragment>
                    <Alert severity={error ? 'error' : 'info'} sx={{ mb: 3 }}>
                        {error || 'Licitação não encontrada.'}
                    </Alert>
                    {backButton}
                </React.Fragment>
            ) : (
                <Paper sx={{ p: 4, mb: 3 }} variant="outlined">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="h5" component="h1" fontWeight={600}>
                            {number}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip icon={<VerifiedIcon />} label="PNCP" color="success" size="medium" />
                            {purchase.situationName && (
                                <Chip label={purchase.situationName} color="info" size="medium" />
                            )}
                            {purchase.indSrp && <Chip label="Registro de Preços" variant="outlined" size="medium" />}
                            {biddingDetail?.externalUrl && (
                                <Button
                                    variant="outlined"
                                    endIcon={<OpenInNewIcon />}
                                    onClick={() => openExternal(biddingDetail.externalUrl)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Ver no PNCP
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <InfoCard icon={<DescriptionIcon color="primary" />} label="Objeto">
                                <Typography variant="body1">{purchase.objectPurchase || 'Não informado'}</Typography>
                            </InfoCard>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <InfoCard icon={<BusinessIcon color="primary" />} label="Órgão / Unidade">
                                <Typography variant="body1">{purchase.orgaoEntidade?.companyName || 'Não informado'}</Typography>
                                {purchase.unidadeOrgao?.name && (
                                    <Typography variant="caption" color="text.secondary">
                                        {purchase.unidadeOrgao.unitCode ? `${purchase.unidadeOrgao.unitCode} - ` : ''}{purchase.unidadeOrgao.name}
                                    </Typography>
                                )}
                            </InfoCard>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <InfoCard icon={<GavelIcon color="primary" />} label="Modalidade">
                                <Typography variant="body1">{purchase.modalityName || 'Não informado'}</Typography>
                                {purchase.disputeModeName && (
                                    <Typography variant="caption" color="text.secondary">
                                        Modo de disputa: {purchase.disputeModeName}
                                    </Typography>
                                )}
                            </InfoCard>
                        </Grid>

                        {purchase.processNumber && (
                            <Grid item xs={12} md={6}>
                                <InfoCard icon={<FolderOpenIcon color="primary" />} label="Número do Processo">
                                    <Typography variant="body1">{purchase.processNumber}</Typography>
                                </InfoCard>
                            </Grid>
                        )}

                        {purchase.typeCallingInstrument && (
                            <Grid item xs={12} md={6}>
                                <InfoCard icon={<DescriptionIcon color="primary" />} label="Instrumento Convocatório">
                                    <Typography variant="body1">{purchase.typeCallingInstrument}</Typography>
                                </InfoCard>
                            </Grid>
                        )}

                        {purchase.amountEstimated != null && (
                            <Grid item xs={12} md={6}>
                                <InfoCard icon={<AttachMoneyIcon color="primary" />} label="Valor Estimado">
                                    <Typography variant="h6" fontWeight={600}>{formatCurrency(purchase.amountEstimated)}</Typography>
                                </InfoCard>
                            </Grid>
                        )}

                        {purchase.amountHomologated != null && (
                            <Grid item xs={12} md={6}>
                                <InfoCard icon={<AttachMoneyIcon color="primary" />} label="Valor Homologado">
                                    <Typography variant="h6" fontWeight={600}>{formatCurrency(purchase.amountHomologated)}</Typography>
                                </InfoCard>
                            </Grid>
                        )}

                        {purchase.numberControlePncp && (
                            <Grid item xs={12} md={6}>
                                <InfoCard icon={<VerifiedIcon color="primary" />} label="Número de Controle PNCP">
                                    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>{purchase.numberControlePncp}</Typography>
                                </InfoCard>
                            </Grid>
                        )}

                        {purchase.informationComplementary && (
                            <Grid item xs={12}>
                                <InfoCard icon={<DescriptionIcon color="primary" />} label="Informações Complementares">
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{purchase.informationComplementary}</Typography>
                                </InfoCard>
                            </Grid>
                        )}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon />
                        Datas Importantes
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {[
                            { label: 'Publicação no PNCP', value: formatDate(purchase.publishedPncpOn) },
                            { label: 'Início das Propostas', value: formatDate(purchase.proposalOpeningDate, 'DD/MM/YYYY HH:mm') },
                            { label: 'Fim das Propostas', value: formatDate(purchase.proposalCloseDate, 'DD/MM/YYYY HH:mm') },
                        ].map((date) => (
                            <Grid item xs={12} sm={4} key={date.label}>
                                <Typography variant="body2" color="text.secondary">
                                    {date.label}
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {date.value}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ListAltIcon />
                        Itens
                    </Typography>

                    {items.length > 0 && (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Nº</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Quantidade</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Valor Unitário</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Valor Total</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Situação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((item) => (
                                        <TableRow key={item.number}>
                                            <TableCell>{item.number}</TableCell>
                                            <TableCell sx={{ minWidth: 220 }}>
                                                <Typography variant="body2">{item.description || '-'}</Typography>
                                                {item.typeItemDescription && (
                                                    <Typography variant="caption" color="text.secondary">{item.typeItemDescription}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {item.quantity?.toLocaleString('pt-BR')} {item.unitMeasure}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(item.unitValueEstimated)}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(item.amount)}</TableCell>
                                            <TableCell>{item.situationDescription || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {loadingItems && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={32} />
                        </Box>
                    )}

                    {itemsError && (
                        <Alert
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <Button color="inherit" size="small" onClick={() => loadItems(biddingDetail!.id, itemsPage + 1)}>
                                    Tentar novamente
                                </Button>
                            }
                        >
                            {itemsError}
                        </Alert>
                    )}

                    {!loadingItems && !itemsError && items.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Nenhum item informado para esta licitação.
                        </Typography>
                    )}

                    {hasMoreItems && !loadingItems && !itemsError && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button variant="outlined" onClick={() => loadItems(biddingDetail!.id, itemsPage + 1)} sx={{ textTransform: 'none' }}>
                                Carregar mais itens
                            </Button>
                        </Box>
                    )}

                    {documents.length > 0 && (
                        <>
                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DescriptionIcon />
                                Documentos ({documents.length})
                            </Typography>

                            <List>
                                {documents.map((doc) => (
                                    <ListItem
                                        key={doc.number}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            gap: 1,
                                            flexWrap: 'wrap',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            }
                                        }}
                                    >
                                        <ListItemIcon>
                                            <DescriptionIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={doc.title || doc.documentTypeDescription || 'Documento'}
                                            secondary={[doc.documentTypeDescription, doc.publishDate && `Publicado em ${formatDate(doc.publishDate)}`]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        />
                                        {doc.fileUrl && (
                                            <Button
                                                variant="outlined"
                                                startIcon={<OpenInNewIcon />}
                                                onClick={() => openExternal(doc.fileUrl)}
                                                size="small"
                                            >
                                                Abrir
                                            </Button>
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}

                    <Divider sx={{ my: 4 }} />

                    {backButton}
                </Paper>
            )}
        </Box>
    );
}
